import { useCallback, useMemo, useState } from 'react';

import { useGetNoTopSlotsQuery, useGetTopSlotsQuery } from '../../api/queries/topslot';
import { NoTopSlot } from '../../interfaces/NoTopSlot';
import { Ticket } from '../../interfaces/Ticket';
import { SlotsDictionary, TopSlot } from '../../interfaces/TopSlot';

export interface UseTopSlotsResult {
  areAnySlotsLoading: boolean;
  areTopSlotsLoading: boolean;
  availableDateEnd: Date;
  fetchMoreTopSlots: () => void;
  highestSlot: TopSlot;
  noTopSlots: NoTopSlot[];
  refetchNoTopSlots: () => void;
  topSlots: SlotsDictionary;
}

export const useTopSlots = (minDate: Date, maxDate: Date, ticket: Ticket | undefined): UseTopSlotsResult => {
  // `newMinDate` and `maxDate` are 2 values that store timeframe that we ask a service to request data for.
  // When we want to get more data, we adjust `newMinDate` and allow a service request
  // data for as many weeks as needed starting from the `minDate`
  const [newMinDate, setMinDates] = useState(minDate);

  const { isLoading: areTopSlotsLoading, availableDateEnd, topSlots, highestSlot } = useGetTopSlotsQuery({
    hashId: ticket?.hashId,
    serviceAppointmentId: ticket?.workOrder.appointment.id,
    minDate: newMinDate,
    maxDate: maxDate,
  });

  const hasTopSlots = !!Object.keys(topSlots).length;
  const shouldFetchNoTopSlots = !hasTopSlots && !areTopSlotsLoading;

  const { getNoTopSlotsResult, isLoading: areNoTopSlotsLoading, refetch: refetchNoTopSlots } = useGetNoTopSlotsQuery({
    hashId: ticket?.hashId,
    workOrderId: ticket?.workOrder.id,
    shouldFetchNoTopSlots
  });

  const fetchMoreTopSlots = useCallback(() => {
    setMinDates(availableDateEnd);
  }, [availableDateEnd]);

  const filteredNoTopSlots = useMemo(() => getNoTopSlotsResult?.noTopSlots?.filter(
    noTopSlot => !!noTopSlot.periods.length) || [], [getNoTopSlotsResult]) ;
  const hasNoTopSlots = !!filteredNoTopSlots.length;

  return {
    areAnySlotsLoading: (!hasTopSlots && areTopSlotsLoading) || (!hasNoTopSlots && areNoTopSlotsLoading),
    areTopSlotsLoading,
    topSlots,
    noTopSlots: filteredNoTopSlots,
    highestSlot,
    availableDateEnd,
    refetchNoTopSlots,
    fetchMoreTopSlots,
  };
};
